/* ScheduleRenderer.js */

"use strict";
const _ = require("lodash");
const InputRadioControl = require("@gluenetworks/input_radio_control");
const InputNumberControl = require("@gluenetworks/input_number_control");
const ComboBoxControl = require("@gluenetworks/combobox").Single;
const BaseScheduleRenderer = require("./base/BaseScheduleRenderer");
const RendererUtils = require("./base/RendererUtils");

const singularRepeatOptions = [
    {label: "hour", value: "repeatHour"},
    {label: "day", value: "repeatDay"},
    {label: "week", value: "repeatWeek"},
    {label: "month", value: "repeatMonth"},
    {label: "year", value: "repeatYear"},
];

const pluralRepeatOptions = [
    {label: "hours", value: "repeatHour"},
    {label: "days", value: "repeatDay"},
    {label: "weeks", value: "repeatWeek"},
    {label: "months", value: "repeatMonth"},
    {label: "years", value: "repeatYear"},
];

const dayOrDowOptions = [
    {label: "date", value: "date"},
    {label: "day of the week", value: "week"},
];

const startDatePropNames = ["year", "month", "date", "hour", "minute", "second"];

class ScheduleRenderer extends BaseScheduleRenderer {
    constructor(model) {
        super(model);
        this._rootElem = document.createElement("schedule-component-gl");
        this._rootElem.component = this._model;
        this._disabledMask = document.createElement("disabled-mask-gl");
        this._rootElem.appendChild(this._disabledMask);
        this._rememberedValues = {};
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    render() {
        if (this.isRendered()) {
            return Promise.resolve();
        }
        return Promise.resolve()
            .then(() => {
                let options = this._model.getOptions();

                this._oneOrRepeatComp = new InputRadioControl({
                    horizontal: true,
                    theme: this._model.theme,
                    options: [
                        {label: "One Time", value: "one"},
                        {label: "Repeating", value: "repeat"},
                    ],
                });
                this._oneOrRepeatComp.renderer.rootElement.classList.add("type-choice");
                this._rootElem.appendChild(this._oneOrRepeatComp.renderer.rootElement);
                if (options.allowOne && options.allowRepeat) {
                    this._oneOrRepeatComp.renderer.rootElement.classList.add("display");
                }

                if (typeof options.disabled !== "undefined") {
                    this._toggleScheduleAvailability(options.disabled);
                }

                this._gridContainerElem = document.createElement("schedule-grid-gl");
                this._gridContainerElem.classList.add("grid-container");
                this._rootElem.appendChild(this._gridContainerElem);

                this._repeatEveryComp = new InputNumberControl({value: 1, min: 1, max: 99});
                this._repeatPeriodComp = new ComboBoxControl({
                    options: singularRepeatOptions,
                    showFilter: false,
                    adjustWidth: true,
                });
                this._dateOrWeekComp = new ComboBoxControl({
                    options: dayOrDowOptions,
                    value: "date",
                    showFilter: false,
                    adjustWidth: true,
                });

                this._everyParts = RendererUtils.groupGridRowElements(
                    this._gridContainerElem,
                    "every",
                    "Every",
                    this._repeatEveryComp,
                    this._repeatPeriodComp,
                    "on a",
                    this._dateOrWeekComp
                );
                if (options.pollOnly) {
                    this._repeatPeriodComp.disabled = true;
                }
                this._repeatLabelElem = this._everyParts[0];
                this._repeatPeriodLabel = this._everyParts[3];
                this._repeatContainerElem = _.last(this._everyParts);

                this._currScheduleComponent = undefined;
                return Promise.all([
                    this._oneOrRepeatComp.renderer.render(),
                    this._repeatPeriodComp.renderer.render(),
                    this._repeatEveryComp.renderer.render(),
                ]);
            })
            .then(() => {
                return this._renderCurrentSchedule(this._model.getSchedule());
            })
            .then(() => {
                this._model.setSchedule(this._model.getSchedule(), true);
                this._setTheme(this._model.theme);
                this._registerListeners();
                this.setRendered(true);
            });
    }

    getParts() {
        return this._everyParts.concat(
            this._rootElem,
            this._oneOrRepeatComp,
            this._gridContainerElem,
            this._currScheduleComponent,
            this._dateOrWeekComp
        );
    }

    shutdown() {
        if (!this.isRendered()) {
            return Promise.resolve();
        }
        return Promise.resolve()
            .then(() => {
                this._unregisterListeners();
                return Promise.all([
                    super.shutdown(),
                    this._oneOrRepeatComp.shutdown(),
                    this._repeatPeriodComp.shutdown(),
                    this._repeatEveryComp.shutdown(),
                    this._dateOrWeekComp.shutdown(),
                    this._currScheduleComponent.shutdown(),
                ]);
            })
            .then(() => {
                this._everyParts = undefined;
                this._disabledMask = undefined;
                this._gridContainerElem.remove();
                this._gridContainerElem = undefined;
                this._oneOrRepeatComp = undefined;
                this._repeatEveryComp = undefined;
                this._dateOrWeekComp = undefined;
                this._currScheduleComponent = undefined;
                this._prev = undefined;
            })
            .catch((err) => {
                console.error(err);
            });
    }

    _registerListeners() {
        super._registerListeners();
        this._oneOrRepeatComp.on(
            "UserValue",
            (this._onOneOrRepeat = this._onOneOrRepeat.bind(this))
        );
        this._repeatPeriodComp.on(
            "UserValue",
            (this._onRepeatPeriod = this._onRepeatPeriod.bind(this))
        );
        this._repeatEveryComp.on("UserValue", (this._onEvery = this._onEvery.bind(this)));
        this._dateOrWeekComp.on("UserValue", (this._onDateOrWeek = this._onDateOrWeek.bind(this)));
        this._model.on("TypeChoice", (this._onTypeChoice = this._onTypeChoice.bind(this)));
        this._model.on("ReloadRender", (this._updateRender = this._updateRender.bind(this)));
        this._model.on(
            "ToggleScheduleAvailability",
            (this._toggleScheduleAvailability = this._toggleScheduleAvailability.bind(this))
        );
    }

    _unregisterListeners() {
        super._unregisterListeners();
        this._oneOrRepeatComp.removeListener("UserValue", this._onOneOrRepeat);
        this._repeatPeriodComp.removeListener("UserValue", this._onRepeatPeriod);
        this._repeatEveryComp.removeListener("UserValue", this._onEvery);
        this._dateOrWeekComp.removeListener("UserValue", this._onDateOrWeek);
        this._model.removeListener("TypeChoice", this._onTypeChoice);
        this._model.removeListener("ReloadRender", this._updateRender);
        this._model.removeListener("ToggleDisableSchedule", this._toggleScheduleAvailability);
    }

    _onOneOrRepeat(newVal, oldVal) {
        if (newVal !== oldVal) {
            let newSched;
            if (newVal === "repeat") {
                newSched = this._model.getScheduleTypeInfo("day").compClass.defaultValue();
            } else {
                newSched = this._model.getScheduleTypeInfo("one").compClass.defaultValue();
            }
            newSched = this._copyRememberedValues(newSched);
            return this._renderCurrentSchedule(newSched).then(() => {
                this._model.setSchedule(newSched, true);
            });
        }
    }

    _onEvery(newVal, oldVal) {
        if (newVal !== oldVal) {
            this._repeatPeriodComp.setOptions(
                this._repeatEveryComp.value === 1 ? singularRepeatOptions : pluralRepeatOptions
            );
            this._repeatPeriodComp.setValue(this._repeatPeriodComp.value);
            this._model.setScheduleProp(
                this._model.getSchedule().type === "poll" ? "every.hour" : "every",
                newVal,
                true
            );
        }
    }

    _onRepeatPeriod(newPeriod, oldPeriod) {
        if (newPeriod !== oldPeriod) {
            this._setCurrentSchedule();
        }
    }

    _onDateOrWeek(newVal, oldVal) {
        if (newVal !== oldVal) {
            this._setCurrentSchedule();
        }
    }

    _onTypeChoice(/* newVal, oldVal */) {
        this._setAllowChoice();
    }

    _setAllowChoice() {
        let options = this._model.getOptions();
        if (options.allowOne && options.allowRepeat) {
            this._oneOrRepeatComp.renderer.rootElement.classList.add("display");
        } else {
            this._oneOrRepeatComp.renderer.rootElement.classList.remove("display");
        }
    }

    /**
     * @private
     * @param {Boolean} disableOption
     */
    _toggleScheduleAvailability(disableOption) {
        if (!disableOption && this._disabledMask.classList.contains("visible")) {
            this._disabledMask.classList.remove("visible");
        } else if (disableOption === true && !this._disabledMask.classList.contains("visible")) {
            this._disabledMask.classList.add("visible");
        }
    }

    _setCurrentSchedule(isReloading, isFromUpdate = false) {
        let schedPeriod = this._repeatPeriodComp.getValue();
        let newSched;
        switch (schedPeriod) {
            case undefined:
            case "one":
                newSched = this._model.getScheduleTypeInfo("one").compClass.defaultValue();
                break;
            case "repeatHour":
                newSched = this._model.getScheduleTypeInfo("poll").compClass.defaultValue();
                break;
            case "repeatDay":
                newSched = this._model.getScheduleTypeInfo("day").compClass.defaultValue();
                break;
            case "repeatWeek":
                newSched = this._model.getScheduleTypeInfo("dow").compClass.defaultValue();
                break;
            case "repeatMonth":
                if (this._dateOrWeekComp.getValue() === "date") {
                    newSched = this._model.getScheduleTypeInfo("dom").compClass.defaultValue();
                } else {
                    newSched = this._model.getScheduleTypeInfo("dowom").compClass.defaultValue();
                }
                break;
            case "repeatYear":
                if (this._dateOrWeekComp.getValue() === "date") {
                    newSched = this._model.getScheduleTypeInfo("domoy").compClass.defaultValue();
                } else {
                    newSched = this._model.getScheduleTypeInfo("dowomoy").compClass.defaultValue();
                }
                break;
            case "avail":
                newSched = this._model.getScheduleTypeInfo("avail").compClass.defaultValue();
                break;
        }
        if (newSched) {
            if (newSched.every && _.isObject(newSched.every) && newSched.every.hour) {
                newSched.every.hour = this._repeatEveryComp.getValue();
            } else if (newSched.every) {
                newSched.every = this._repeatEveryComp.getValue();
            }
            newSched = this._copyRememberedValues(newSched, isFromUpdate);
            return this._renderCurrentSchedule(newSched).then(() => {
                this._model.setSchedule(newSched, true);
            });
        }
    }

    _copyRememberedValues(newSched, isFromUpdate) {
        let currSched = this._model.getSchedule() || {};
        _.assign(
            this._rememberedValues,
            _.pick(currSched, "on", "time", "start", "end", "count", "minute", "tz")
        );
        if (isFromUpdate) {
            newSched = Object.assign({}, newSched, currSched);
        }

        if (currSched.on) {
            _.set(this._rememberedValues, "time.hour", currSched.on.hour);
            _.set(this._rememberedValues, "time.minute", currSched.on.minute);
        } else if (currSched.time && this._rememberedValues.on) {
            this._rememberedValues.on.hour = currSched.time.hour;
            this._rememberedValues.on.minute = currSched.time.minute;
        }

        if (currSched.count) {
            delete this._rememberedValues.end;
        } else if (currSched.end) {
            delete this._rememberedValues.count;
        }

        if (this._rememberedValues.time && newSched.time) {
            newSched.time = this._rememberedValues.time;
        }

        if (this._rememberedValues.minute) {
            newSched.minute = this._rememberedValues.minute;
        }

        if (this._rememberedValues.tz) {
            newSched.tz = this._rememberedValues.tz;
        }

        if (newSched.on) {
            if (this._rememberedValues.on) {
                newSched.on = this._rememberedValues.on;
            } else if (this._rememberedValues.time) {
                newSched.on.hour = this._rememberedValues.time.hour;
                newSched.on.minute = this._rememberedValues.time.minute;
            }
        }

        if (this._rememberedValues.start && newSched.start) {
            startDatePropNames.forEach((propName) => {
                if (
                    Object.hasOwn(newSched.start, propName) &&
                    Object.hasOwn(this._rememberedValues.start, propName)
                ) {
                    newSched.start[propName] = this._rememberedValues.start[propName];
                }
            });
            if (this._rememberedValues.end) {
                newSched.end = this._rememberedValues.end;
            } else if (this._rememberedValues.count) {
                newSched.count = this._rememberedValues.count;
            }
        }
        return newSched;
    }

    _renderCurrentSchedule(newSched) {
        return Promise.resolve()
            .then(() => {
                if (this._currScheduleComponent) {
                    RendererUtils.removeChildElements(
                        this._gridContainerElem,
                        this._currScheduleComponent.renderer.getParts()
                    );
                    return this._currScheduleComponent.shutdown();
                }
            })
            .then(() => {
                this._model.setSchedule(newSched, true);
                let schedTypeInfo = this._model.getScheduleTypeInfo(newSched.type);
                this._currScheduleComponent = new schedTypeInfo.compClass(this._model);
                this._currScheduleComponent.theme = this.model.theme;
                if (schedTypeInfo.repeatOption) {
                    this._repeatPeriodComp.setValue(schedTypeInfo.repeatOption);
                }
                if (newSched.every) {
                    this._repeatEveryComp.setValue(
                        _.get(newSched, newSched.type === "poll" ? "every.hour" : "every", 1)
                    );
                }

                let newOneOrRepeatVal = newSched.type === "one" ? "one" : "repeat";
                if (this._oneOrRepeatComp.getValue() !== newOneOrRepeatVal) {
                    this._oneOrRepeatComp.setValue(newOneOrRepeatVal);
                }

                let schedType = newSched.type;
                if (
                    schedType === "one" ||
                    (!this._model.options.allowRepeat && !this._model.options.pollOnly)
                ) {
                    RendererUtils.removeChildElements(
                        this._gridContainerElem,
                        this._repeatLabelElem,
                        this._repeatContainerElem
                    );
                } else {
                    RendererUtils.appendChildElements(
                        this._gridContainerElem,
                        this._repeatLabelElem,
                        this._repeatContainerElem
                    );
                }

                if (
                    schedType === "dom" ||
                    schedType === "dowom" ||
                    schedType === "domoy" ||
                    schedType === "dowomoy"
                ) {
                    RendererUtils.showElements(this._repeatPeriodLabel, this._dateOrWeekComp);
                    this._dateOrWeekComp.setValue(
                        schedType === "dom" || schedType === "domoy" ? "date" : "week"
                    );
                } else {
                    RendererUtils.hideElements(this._repeatPeriodLabel, this._dateOrWeekComp);
                }
                return this._currScheduleComponent.renderer.render(this._gridContainerElem);
            });
    }

    _updateRender() {
        //set repeat period
        const scheduleValues = this._model.getSchedule();
        let schedTypeInfo = this._model.getScheduleTypeInfo(scheduleValues.type);
        this._repeatPeriodComp.setValue(schedTypeInfo.repeatOption);
        this._repeatEveryComp.setValue(
            _.get(scheduleValues, scheduleValues.type === "poll" ? "every.hour" : "every", 1)
        );
        //rerender current schedule
        this._setCurrentSchedule(undefined, true);
    }

    _setControlValuesFromModel() {
        // setByUser = true
    }
}

module.exports = ScheduleRenderer;
